import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { 
  CreditCard, Upload, Plus, Trash2, Loader2, Wallet
} from 'lucide-react';
import ExportButton from './ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExpensesManagement = ({ token }) => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shareholderSummary, setShareholderSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [filter, setFilter] = useState({ shareholder: '', category: '' });
  const [newExpense, setNewExpense] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    amount: '',
    currency: 'CAD',
    shareholder_account: '',
    notes: ''
  });

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchShareholderSummary();
  }, [filter]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/expenses?`;
      if (filter.shareholder) url += `shareholder=${filter.shareholder}&`;
      if (filter.category) url += `category=${filter.category}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/expenses/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchShareholderSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/expenses/shareholder-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShareholderSummary(response.data);
    } catch (error) {
      console.error('Error fetching shareholder summary:', error);
    }
  };

  const handleAddExpense = async () => {
    try {
      await axios.post(`${API}/admin/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount),
        shareholder_account: newExpense.shareholder_account || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dépense ajoutée');
      setShowAddDialog(false);
      setNewExpense({
        date: new Date().toISOString().split('T')[0],
        category: '',
        description: '',
        amount: '',
        currency: 'CAD',
        shareholder_account: '',
        notes: ''
      });
      fetchExpenses();
      fetchShareholderSummary();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!confirm('Supprimer cette dépense ?')) return;
    try {
      await axios.delete(`${API}/admin/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dépense supprimée');
      fetchExpenses();
      fetchShareholderSummary();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/expenses/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${response.data.imported} dépenses importées`);
      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} erreurs`);
      }
      setShowImportDialog(false);
      fetchExpenses();
      fetchShareholderSummary();
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-6">
      {/* Shareholder Accounts Summary */}
      {shareholderSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Compte Yan</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(shareholderSummary.yan.total)}</p>
                  <p className="text-xs text-blue-600">{shareholderSummary.yan.count} avances</p>
                </div>
                <Wallet className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Compte Eric</p>
                  <p className="text-2xl font-bold text-purple-700">{formatCurrency(shareholderSummary.eric.total)}</p>
                  <p className="text-xs text-purple-600">{shareholderSummary.eric.count} avances</p>
                </div>
                <Wallet className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions & Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex flex-wrap gap-2">
              <select
                value={filter.shareholder}
                onChange={(e) => setFilter({...filter, shareholder: e.target.value})}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">Tous les comptes</option>
                <option value="yan">Compte Yan</option>
                <option value="eric">Compte Eric</option>
              </select>
              <select
                value={filter.category}
                onChange={(e) => setFilter({...filter, category: e.target.value})}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="">Toutes catégories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <ExportButton token={token} exportType="expenses" />
              <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Importer CSV
              </Button>
              <Button onClick={() => setShowAddDialog(true)} className="bg-brand-600">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expenses List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-brand-600" />
            Dépenses ({expenses.length}) - Total: {formatCurrency(totalExpenses)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : expenses.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Catégorie</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Description</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Compte</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Montant</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => (
                    <tr key={expense.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{expense.date?.split('T')[0]}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">{expense.category}</span>
                      </td>
                      <td className="py-3 px-4">{expense.description}</td>
                      <td className="py-3 px-4">
                        {expense.shareholder_account && (
                          <span className={`px-2 py-1 rounded text-sm font-medium ${
                            expense.shareholder_account === 'yan' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {expense.shareholder_account.charAt(0).toUpperCase() + expense.shareholder_account.slice(1)}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-red-600">
                        -{formatCurrency(expense.amount)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Aucune dépense trouvée
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Expense Dialog */}
      <AlertDialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Ajouter une dépense</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newExpense.date}
                  onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Montant</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Input
                value={newExpense.category}
                onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
                placeholder="Ex: Marketing, Serveurs, Salaires..."
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newExpense.description}
                onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                placeholder="Description de la dépense"
              />
            </div>
            <div className="space-y-2">
              <Label>Compte actionnaire (optionnel)</Label>
              <select
                value={newExpense.shareholder_account}
                onChange={(e) => setNewExpense({...newExpense, shareholder_account: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Aucun (dépense entreprise)</option>
                <option value="yan">Avance par Yan</option>
                <option value="eric">Avance par Eric</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={newExpense.notes}
                onChange={(e) => setNewExpense({...newExpense, notes: e.target.value})}
                placeholder="Notes optionnelles"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleAddExpense} className="bg-brand-600">
              Ajouter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import CSV Dialog */}
      <AlertDialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Importer des dépenses</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-4 pt-4">
                <p>Format CSV attendu (colonnes):</p>
                <code className="block bg-gray-100 p-2 rounded text-xs">
                  date, category, description, amount, currency, shareholder, notes
                </code>
                <p className="text-sm">
                  <strong>shareholder:</strong> "yan" ou "eric" pour les avances d'actionnaires
                </p>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpensesManagement;
